import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container, Button, Image, Badge, SegmentedControl,
  Accordion, TextInput, Textarea, Select, FileInput, FileButton,
  ActionIcon, Stack, Group, Divider, Text, Box, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconInfoCircle, IconUsers, IconFolder, IconFile, IconPhoto,
  IconDownload, IconEye, IconTrash, IconMap, IconShoppingCart,
  IconCheck, IconAlertCircle, IconPlus, IconX, IconEdit, IconExternalLink, IconLink,
} from '@tabler/icons-react';
import { getUserFullDetails } from '../Redux/user/slice';
import { DropzoneButton } from './DropzoneButton.js';
import { BASE_URL } from '../Redux/apiConfig';
import { FormattedMessage, useIntl } from 'react-intl';

const SUB_TABS = [
  { id: 'userInfo',            Icon: IconInfoCircle, label: 'User Info'  },
  { id: 'profilePresentation', Icon: IconUsers,      label: 'Profile'    },
  { id: 'files',               Icon: IconFolder,     label: 'Documents'  },
];

const normalizeInfoGrid = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const mapping = {
      producer: 'Producer', locationInfo: 'Location', extension: 'Extension',
      mainCrop: 'Main Crop', experience: 'Experience', practices: 'Practices',
      certifications: 'Certifications', altitude: 'Altitude',
    };
    return Object.entries(mapping).map(([key, label]) => ({ label, value: data[key] || '' }));
  }
  return [
    { label: 'Producer', value: '' }, { label: 'Location', value: '' },
    { label: 'Extension', value: '' }, { label: 'Main Crop', value: '' },
    { label: 'Experience', value: '' }, { label: 'Practices', value: '' },
    { label: 'Certifications', value: '' }, { label: 'Altitude', value: '' },
  ];
};

const uploadFile = async (accountId, fileName, file) => {
  const authToken = localStorage.getItem('authToken');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64Data = reader.result.split(',')[1];
        const response = await fetch(`${BASE_URL}/api/upload-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'apikey': 'abc123',
          },
          body: JSON.stringify({ accountId, fileName, fileData: base64Data, fileType: file.type }),
        });
        if (response.ok) resolve(await response.json());
        else reject(new Error(`Upload failed: ${response.status}`));
      } catch (e) { reject(e); }
    };
    reader.readAsDataURL(file);
  });
};

const rowStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #f5f5f5',
};
const labelStyle = { fontWeight: 600, fontSize: '0.78rem', color: '#555', fontFamily: "'Montserrat', sans-serif" };
const valueStyle = { fontSize: '0.78rem', color: '#222', textAlign: 'right', maxWidth: '55%', wordBreak: 'break-all', fontFamily: "'Montserrat', sans-serif" };
const actionBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#555', padding: '4px', display: 'flex', alignItems: 'center' };

export default function MobileMyPersonalSpace() {
  const intl = useIntl();
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  const accountId = userinfo?.accountId;
  const userFullDetails = useSelector((state) => state.user.userFullDetails);
  const dispatch = useDispatch();

  const [copySuccess, setCopySuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('userInfo');
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [description, setDescription] = useState('');
  const [infoGrid, setInfoGrid] = useState(normalizeInfoGrid(null));
  const [location, setLocation] = useState({ address: '', description: '', mapUrl: '', coordinates: '', coordinatesDMS: '' });
  const [gallery, setGallery] = useState([]);
  const [productLots, setProductLots] = useState([]);
  const [contact, setContact] = useState({ name: '', phone: '', email: '', address: '' });
  const [socialLinks, setSocialLinks] = useState({ instagram: '', twitter: '', website: '' });
  const [headerColor, setHeaderColor] = useState('brown');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [usdcBalance, setUSDCBalance] = useState(0);
  const [hbarBalance, setHBARBalance] = useState(0);
  const [hasCustomTemplate, setHasCustomTemplate] = useState(false);
  const [previewMode, setPreviewMode] = useState('custom');

  // Auto-switch to custom mode only for first-time users (no saved preference yet)
  useEffect(() => {
    if (hasCustomTemplate && !profileLoaded) setPreviewMode('custom');
  }, [hasCustomTemplate, profileLoaded]);

  const makeLocalSetter = (setter, key, shouldStringify = false) => (value) => {
    setter(value);
    if (accountId) {
      localStorage.setItem(`profile_${key}_${accountId}`, shouldStringify ? JSON.stringify(value) : value);
    }
  };
  const setTitleWithSave       = makeLocalSetter(setTitle,       'title');
  const setSubtitleWithSave    = makeLocalSetter(setSubtitle,    'subtitle');
  const setHeroImageWithSave   = makeLocalSetter(setHeroImage,   'heroImage');
  const setDescriptionWithSave = makeLocalSetter(setDescription, 'description');
  const setGalleryWithSave     = makeLocalSetter(setGallery,     'gallery',      true);
  const setInfoGridWithSave    = makeLocalSetter(setInfoGrid,    'infoGrid',     true);
  const setLocationWithSave    = makeLocalSetter(setLocation,    'location',     true);
  const setProductLotsWithSave = makeLocalSetter(setProductLots, 'productLots',  true);
  const setContactWithSave     = makeLocalSetter(setContact,     'contact',      true);
  const setSocialLinksWithSave = makeLocalSetter(setSocialLinks, 'socialLinks',  true);
  const setHeaderColorWithSave = makeLocalSetter(setHeaderColor, 'headerColor');

  useEffect(() => {
    if (!userFullDetails.userId) dispatch(getUserFullDetails({ email: userinfo.email }));
  }, [dispatch, userinfo.email, userFullDetails.userId]);

  useEffect(() => {
    if (activeTab === 'files') fetchFiles();
  }, [activeTab]);

  useEffect(() => {
    if (!accountId) return;
    const get = (key) => localStorage.getItem(`profile_${key}_${accountId}`);
    const getJSON = (key) => { try { const v = get(key); return v ? JSON.parse(v) : null; } catch { return null; } };
    if (get('title'))       setTitle(get('title'));
    if (get('subtitle'))    setSubtitle(get('subtitle'));
    if (get('heroImage'))   setHeroImage(get('heroImage'));
    if (get('description')) setDescription(get('description'));
    if (getJSON('gallery'))     setGallery(getJSON('gallery'));
    if (getJSON('infoGrid'))    setInfoGrid(normalizeInfoGrid(getJSON('infoGrid')));
    if (getJSON('location'))    setLocation(getJSON('location'));
    if (getJSON('productLots')) setProductLots(getJSON('productLots'));
    if (getJSON('contact'))     setContact(getJSON('contact'));
    if (getJSON('socialLinks')) setSocialLinks(getJSON('socialLinks'));
    if (get('headerColor'))     setHeaderColor(get('headerColor'));
    const hasLocal = ['title','subtitle','heroImage','description'].some(k => !!get(k));
    if (hasLocal) setProfileLoaded(true);
  }, [accountId]);

  useEffect(() => {
    if (!accountId || profileLoaded) return;
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/profile/${accountId}`);
        if (response.ok) {
          const data = await response.json();
          const get = (key) => localStorage.getItem(`profile_${key}_${accountId}`);
          if (!get('title'))       setTitle(data.title || '');
          if (!get('subtitle'))    setSubtitle(data.subtitle || '');
          if (!get('heroImage'))   setHeroImage(data.heroImage || '');
          if (!get('description')) setDescription(data.description || '');
          if (!get('gallery'))     setGallery(data.gallery || []);
          if (!get('infoGrid'))    setInfoGrid(normalizeInfoGrid(data.infoGrid));
          if (!get('location'))    setLocation(data.location || { address: '', description: '', mapUrl: '', coordinates: '', coordinatesDMS: '' });
          if (!get('productLots')) setProductLots(data.productLots || []);
          if (!get('contact'))     setContact(data.contact || { name: '', phone: '', email: '', address: '' });
          if (!get('socialLinks')) setSocialLinks(data.socialLinks || { instagram: '', twitter: '', website: '' });
          if (!get('headerColor')) setHeaderColor(data.headerColor || 'brown');
          // Always restore the user's saved display mode preference
          setPreviewMode(data.profileDisplayMode || 'custom');
          setProfileLoaded(true);
        }
      } catch (e) { console.error('Error fetching profile:', e); }
    };
    fetchProfile();
  }, [accountId, profileLoaded]);

  useEffect(() => {
    if (!accountId) return;
    const checkCustomTemplate = async () => {
      try {
        const url = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/ProfilePresentation.html?t=${Date.now()}`;
        const response = await fetch(url, { cache: 'no-store' });
        if (response.ok) {
          const html = await response.text();
          const t = html.trim().toLowerCase();
          setHasCustomTemplate(t.length > 50 && (t.includes('<html') || t.includes('<!doctype')));
        } else {
          setHasCustomTemplate(false);
        }
      } catch { setHasCustomTemplate(false); }
    };
    checkCustomTemplate();
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    const fetchUSDCBalance = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/getUSDCBalance/${accountId}`);
        if (response.ok) { const data = await response.json(); setUSDCBalance(data.balance); }
      } catch {}
    };
    fetchUSDCBalance();
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    const fetchHBARBalance = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/getHBARBalance/${accountId}`);
        if (response.ok) { const data = await response.json(); setHBARBalance(data.balance); }
      } catch {}
    };
    fetchHBARBalance();
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    fetch(`${BASE_URL}/api/getProfileQRCode/${accountId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.qrCodeUrl) setQrCodeUrl(`${data.qrCodeUrl}?t=${Date.now()}`); })
      .catch(() => {});
  }, [accountId]);

  const fetchFiles = async () => {
    setLoadingFiles(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/api/documents/${accountId}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}`, 'apikey': 'abc123' },
      });
      setFiles(response.ok ? await response.json() : []);
    } catch { setFiles([]); }
    setLoadingFiles(false);
  };

  const saveProfile = async (overrideMode) => {
    if (!accountId) return;
    setSaveStatus('saving');
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/api/profile/${accountId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}`, 'apikey': 'abc123' },
        body: JSON.stringify({ title, subtitle, heroImage, description, infoGrid, location, gallery, productLots, contact, socialLinks, headerColor, profileDisplayMode: overrideMode ?? previewMode }),
      });
      if (response.ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
        notifications.show({ title: 'Saved!', message: 'Profile saved successfully', color: 'teal', icon: <IconCheck size="1rem" /> });
      } else {
        setSaveStatus('');
        notifications.show({ title: 'Save failed', message: 'Status: ' + response.status, color: 'red', icon: <IconAlertCircle size="1rem" /> });
      }
    } catch {
      setSaveStatus('');
      notifications.show({ title: 'Network error', message: 'Could not save profile', color: 'red', icon: <IconAlertCircle size="1rem" /> });
    }
  };

  const handleHeroImageUpload = async (file) => {
    if (!file) return;
    try {
      const data = await uploadFile(accountId, `hero_${Date.now()}.jpg`, file);
      setHeroImageWithSave(data.url);
      notifications.show({ title: 'Hero image uploaded!', color: 'teal', icon: <IconCheck size="1rem" /> });
    } catch { notifications.show({ title: 'Upload failed', color: 'red', icon: <IconAlertCircle size="1rem" /> }); }
  };

  const handleBackgroundImageUpload = async (file) => {
    if (!file) return;
    try {
      await uploadFile(accountId, 'background.jpg', file);
      notifications.show({ title: 'Background updated!', color: 'teal', icon: <IconCheck size="1rem" /> });
    } catch { notifications.show({ title: 'Upload failed', color: 'red', icon: <IconAlertCircle size="1rem" /> }); }
  };

  const handleGalleryImageUpload = async (file) => {
    if (!file) return;
    try {
      const data = await uploadFile(accountId, `gallery_${Date.now()}.jpg`, file);
      setGalleryWithSave([...gallery, data.url]);
      notifications.show({ title: 'Gallery image uploaded!', color: 'teal', icon: <IconCheck size="1rem" /> });
    } catch { notifications.show({ title: 'Upload failed', color: 'red', icon: <IconAlertCircle size="1rem" /> }); }
  };

  const handleProfileImageUpload = async (file) => {
    if (!file) return;
    const authToken = localStorage.getItem('authToken');
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64Data = reader.result.split(',')[1];
        const response = await fetch(`${BASE_URL}/api/upload-document`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}`, 'apikey': 'abc123' },
          body: JSON.stringify({ accountId, fileName: 'p.png', fileData: base64Data, fileType: file.type }),
        });
        if (response.ok) {
          notifications.show({ title: 'Photo updated!', message: 'Profile photo updated successfully', color: 'teal', icon: <IconCheck size="1rem" /> });
          setTimeout(() => window.location.reload(), 1000);
        } else {
          notifications.show({ title: 'Upload failed', color: 'red', icon: <IconAlertCircle size="1rem" /> });
        }
      } catch { notifications.show({ title: 'Network error', color: 'red', icon: <IconAlertCircle size="1rem" /> }); }
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentDownload = (url, name) => {
    const link = document.createElement('a');
    link.href = url; link.download = name;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const handleDeleteFile = async (fileName) => {
    if (!window.confirm('Delete "' + fileName + '"?')) return;
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${BASE_URL}/api/documents/${accountId}/${encodeURIComponent(fileName)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${authToken}`, 'apikey': 'abc123' },
      });
      if (response.ok) fetchFiles();
    } catch (e) { console.error('Error deleting file:', e); }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/profile_qr.png`;
    link.download = 'profile_qr.png';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const copyPublicKey = () => {
    navigator.clipboard.writeText(userFullDetails.PublicKey);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const fileIcon = (name) => {
    if (name.endsWith('.pdf')) return <IconFile size={20} color="#e53935" />;
    if (/\.(png|jpg|jpeg|avif)$/i.test(name)) return <IconPhoto size={20} color="#1e88e5" />;
    return <IconFile size={20} color="#757575" />;
  };

  const renderUserInfo = () => (
    <div style={{ padding: '4px 0', overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>

      {/* Profile photo with edit button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
        <Box style={{ position: 'relative', display: 'inline-block' }}>
          <Image
            src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/p.png?t=${Date.now()}`}
            fallbackSrc="https://avatar.iran.liara.run/public/boy"
            alt="User Profile Image"
            width={90}
            height={90}
            radius="md"
            fit="cover"
            withPlaceholder
          />
          <label htmlFor="mobile-profile-upload" style={{ position: 'absolute', bottom: '4px', right: '4px', cursor: 'pointer' }}>
            <ActionIcon size="sm" color="blue" variant="filled" component="span" radius="xl"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
              <IconEdit size="0.75rem" />
            </ActionIcon>
          </label>
          <input id="mobile-profile-upload" type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) handleProfileImageUpload(e.target.files[0]); }} />
        </Box>
      </div>

      {/* Name / email / country */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', fontFamily: "'Montserrat', sans-serif", marginBottom: '2px' }}>
          {userFullDetails?.userName || 'User'}
        </div>
        <div style={{ fontSize: '0.78rem', color: '#777', marginBottom: '6px' }}>{userFullDetails?.email || ''}</div>
        <Badge size="sm" variant="light" color="blue">🌍 {userFullDetails?.Country || 'Unknown'}</Badge>
      </div>

      {/* Account Information section */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontFamily: "'Montserrat', sans-serif" }}>
          <FormattedMessage id="userInfo.acctID" defaultMessage="Account Information" />
        </div>
        {[
          ['userInfo.acctID.profile',  userFullDetails?.accountId],
          ['userInfo.prodcontractID',  userFullDetails?.product_contractId],
          ['userInfo.comercontractID', userFullDetails?.commercial_contractId],
        ].map(([id, val]) => (
          <div key={id} style={rowStyle}>
            <span style={labelStyle}><FormattedMessage id={id} /></span>
            <span style={valueStyle}>{val || 'N/A'}</span>
          </div>
        ))}
      </div>

      {/* Blockchain Information section */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontFamily: "'Montserrat', sans-serif" }}>
          Blockchain Information
        </div>

        {/* Hedera address + HashScan */}
        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={labelStyle}><FormattedMessage id="userInfo.hederaAddress" /></span>
            <Button
              component="a"
              href={`https://hashscan.io/${userFullDetails?.userName?.includes('#') ? 'testnet' : 'mainnet'}/account/${userFullDetails?.accountId}`}
              target="_blank" rel="noopener noreferrer"
              size="xs" variant="subtle" compact
              rightIcon={<IconExternalLink size="0.7rem" />}
            >
              HashScan
            </Button>
          </div>
          <span style={{ fontSize: '0.78rem', color: '#222' }}>{userFullDetails?.accountId || 'N/A'}</span>
        </div>

        {/* HBAR balance */}
        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '8px' }}>
          <div style={{ ...labelStyle, marginBottom: '4px' }}><FormattedMessage id="userInfo.balance" /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1rem', fontWeight: 700 }}>{hbarBalance}</span>
            <Badge size="md" color="green" variant="light">ℏ</Badge>
          </div>
        </div>

        {/* USDC balance */}
        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '8px' }}>
          <div style={{ ...labelStyle, marginBottom: '4px' }}><FormattedMessage id="userInfo.usdcBalance" defaultMessage="USDC Balance" /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1rem', fontWeight: 700 }}>{usdcBalance}</span>
            <Badge size="md" color="green" variant="light">USDC</Badge>
          </div>
        </div>

        {/* Public key + copy */}
        <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={labelStyle}><FormattedMessage id="userInfo.pubKey" /></span>
            <Button size="xs" variant="light" onClick={copyPublicKey} disabled={copySuccess} compact>
              {copySuccess
                ? <FormattedMessage id="button.copy.success" />
                : <FormattedMessage id="button.copy" />}
            </Button>
          </div>
          <span style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#495057', wordBreak: 'break-all' }}>
            {userFullDetails?.PublicKey ? `${userFullDetails.PublicKey.substring(0, 40)}...` : 'Not available'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderProfile = () => (
    <div style={{ padding: '4px 0', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)' }}>

      {/* ── PUBLIC PROFILE MODE ─────────────────────────── */}
      <div style={{
        background: hasCustomTemplate ? '#f0f7ff' : '#f5f5f5',
        border: `1px solid ${hasCustomTemplate ? '#90caf9' : '#e0e0e0'}`,
        borderRadius: '10px', padding: '12px', marginBottom: '14px'
      }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontFamily: "'Montserrat', sans-serif" }}>
          <FormattedMessage id="profile.preview.mode.label" defaultMessage="Public Profile Mode" />
        </div>
        {hasCustomTemplate ? (
          <>
            <SegmentedControl
              fullWidth size="xs"
              value={previewMode}
              onChange={(mode) => { setPreviewMode(mode); saveProfile(mode); }}
              data={[
                { value: 'form',   label: <FormattedMessage id="profile.preview.mode.form"   defaultMessage="Form Preview" /> },
                { value: 'custom', label: <FormattedMessage id="profile.preview.mode.custom" defaultMessage="Custom HTML"  /> },
              ]}
            />
            <div style={{ fontSize: '0.68rem', color: '#666', marginTop: '6px' }}>
              <FormattedMessage id="profile.preview.mode.hint" defaultMessage="This also controls what visitors see on your public profile." />
            </div>
          </>
        ) : (
          <div style={{ fontSize: '0.75rem', color: '#888' }}>
            <FormattedMessage id="profile.preview.mode.nohtml" defaultMessage="Using form-based profile. Upload a ProfilePresentation.html file to enable a custom layout." />
          </div>
        )}
      </div>

      {/* ── QR CODE ROW ─────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        {qrCodeUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <Image src={qrCodeUrl} alt="QR" width={60} height={60} />
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
              <button style={{ ...actionBtnStyle, color: '#1e88e5', fontSize: '0.7rem', border: '1px solid #d0d8e4', borderRadius: '6px', padding: '3px 8px' }} onClick={downloadQRCode}>
                <IconDownload size={12} style={{ marginRight: 4 }} /> QR
              </button>
              <button
                style={{ ...actionBtnStyle, color: '#555', fontSize: '0.7rem', border: '1px solid #d0d8e4', borderRadius: '6px', padding: '3px 8px' }}
                title="Copy profile link"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/profile/${accountId}`);
                  notifications.show({ message: 'Profile link copied!', color: 'gray', autoClose: 1800 });
                }}
              >
                <IconLink size={12} style={{ marginRight: 4 }} /> Link
              </button>
            </div>
          </div>
        ) : (
          <Text size="xs" color="dimmed"><FormattedMessage id="profile.qrcode.notfound" /></Text>
        )}
      </div>

      <Accordion multiple defaultValue={[]} styles={{ content: { padding: '8px 0', maxHeight: '320px', overflowY: 'auto' } }}>

        <Accordion.Item value="basic">
          <Accordion.Control icon={<IconInfoCircle size="1rem" />}>
            <FormattedMessage id="profile.section.basic" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="sm">
              <TextInput label={<FormattedMessage id="profile.title.label" />}
                placeholder={intl.formatMessage({ id: 'profile.title.placeholder' })}
                value={title} onChange={(e) => setTitleWithSave(e.target.value)} />
              <TextInput label={<FormattedMessage id="profile.subtitle.label" />}
                placeholder={intl.formatMessage({ id: 'profile.subtitle.placeholder' })}
                value={subtitle} onChange={(e) => setSubtitleWithSave(e.target.value)} />
              <Select label={<FormattedMessage id="profile.headerColor.label" />}
                value={headerColor} onChange={setHeaderColorWithSave} withinPortal
                data={[
                  { value: 'brown',  label: '🟤 Brown Coffee'  },
                  { value: 'green',  label: '🟢 Forest Green'  },
                  { value: 'blue',   label: '🔵 Ocean Blue'    },
                  { value: 'orange', label: '🟠 Sunset Orange' },
                  { value: 'purple', label: '🟣 Royal Purple'  },
                  { value: 'teal',   label: '🔷 Tropical Teal' },
                  { value: 'red',    label: '🔴 Wine Red'      },
                  { value: 'slate',  label: '⚫ Slate Gray'    },
                ]} />
              <Textarea label={<FormattedMessage id="profile.description.label" />}
                placeholder={intl.formatMessage({ id: 'profile.description.placeholder' })}
                value={description} onChange={(e) => setDescriptionWithSave(e.target.value)} minRows={3} />
              <FileInput label="Background" placeholder="Upload background image"
                accept="image/*" icon={<IconPhoto size="1rem" />}
                onChange={handleBackgroundImageUpload} />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="hero">
          <Accordion.Control icon={<IconPhoto size="1rem" />}>
            <FormattedMessage id="profile.section.hero" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="sm">
              {heroImage && <Image src={heroImage} alt="Hero" radius="md" style={{ maxHeight: '140px', objectFit: 'cover' }} />}
              <TextInput label={<FormattedMessage id="profile.heroImage.url" />} placeholder="https://..."
                value={heroImage} onChange={(e) => setHeroImageWithSave(e.target.value)} />
              <Divider label={<FormattedMessage id="profile.divider.or" />} labelPosition="center" />
              <FileInput label={<FormattedMessage id="profile.hero.upload" />}
                placeholder={intl.formatMessage({ id: 'profile.hero.uploadPlaceholder', defaultMessage: 'Choose an image' })}
                accept="image/*" icon={<IconPhoto size="1rem" />} onChange={handleHeroImageUpload} />
              <Text size="xs" color="dimmed"><FormattedMessage id="profile.hero.uploadHint" /></Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="infogrid">
          <Accordion.Control icon={<IconUsers size="1rem" />}>
            <FormattedMessage id="profile.section.infogrid" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="xs">
              {infoGrid.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px' }}>
                  <TextInput style={{ flex: 1 }} placeholder="Label" value={item.label}
                    onChange={(e) => { const u = [...infoGrid]; u[index] = { ...u[index], label: e.target.value }; setInfoGridWithSave(u); }} />
                  <TextInput style={{ flex: 1 }} placeholder="Value" value={item.value}
                    onChange={(e) => { const u = [...infoGrid]; u[index] = { ...u[index], value: e.target.value }; setInfoGridWithSave(u); }} />
                </div>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="location">
          <Accordion.Control icon={<IconMap size="1rem" />}>
            <FormattedMessage id="profile.section.location" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="sm">
              <TextInput label={<FormattedMessage id="profile.location.address" />} value={location.address}
                onChange={(e) => setLocationWithSave({ ...location, address: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.location.description" />} value={location.description}
                onChange={(e) => setLocationWithSave({ ...location, description: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.location.mapUrl" />}
                placeholder="https://www.google.com/maps/embed?pb=..."
                value={location.mapUrl} onChange={(e) => setLocationWithSave({ ...location, mapUrl: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.coordinates.decimal" />} placeholder="1.386139,-77.174028"
                value={location.coordinates} onChange={(e) => setLocationWithSave({ ...location, coordinates: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.coordinates.dms" />}
                value={location.coordinatesDMS} onChange={(e) => setLocationWithSave({ ...location, coordinatesDMS: e.target.value })} />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="gallery">
          <Accordion.Control icon={<IconPhoto size="1rem" />}>
            <FormattedMessage id="profile.section.gallery" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="xs">
              {gallery.map((url, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <TextInput style={{ flex: 1 }} placeholder="Image URL" value={url}
                    onChange={(e) => { const u = [...gallery]; u[index] = e.target.value; setGalleryWithSave(u); }} />
                  <ActionIcon color="red" onClick={() => setGalleryWithSave(gallery.filter((_, i) => i !== index))}>
                    <IconX size="1rem" />
                  </ActionIcon>
                </div>
              ))}
              <Group grow>
                <Button variant="light" size="xs" leftIcon={<IconPlus size="0.85rem" />}
                  onClick={() => setGalleryWithSave([...gallery, ''])}>
                  <FormattedMessage id="profile.gallery.addUrl" />
                </Button>
                <FileButton onChange={handleGalleryImageUpload} accept="image/*">
                  {(props) => (
                    <Button {...props} variant="filled" size="xs" color="teal" leftIcon={<IconPhoto size="0.85rem" />}>
                      <FormattedMessage id="profile.gallery.upload" />
                    </Button>
                  )}
                </FileButton>
              </Group>
              <Text size="xs" color="dimmed"><FormattedMessage id="profile.gallery.uploadHint" /></Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="lots">
          <Accordion.Control icon={<IconShoppingCart size="1rem" />}>
            <FormattedMessage id="profile.section.lots" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="sm">
              {productLots.map((lot, index) => (
                <div key={index} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '10px' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                    <TextInput style={{ flex: 1 }} placeholder="Code (e.g. GGL01)" value={lot.code}
                      onChange={(e) => { const u = [...productLots]; u[index] = { ...u[index], code: e.target.value }; setProductLotsWithSave(u); }} />
                    <ActionIcon color="red" onClick={() => setProductLotsWithSave(productLots.filter((_, i) => i !== index))}>
                      <IconX size="1rem" />
                    </ActionIcon>
                  </div>
                  <TextInput placeholder="Name" value={lot.name} style={{ marginBottom: '6px' }}
                    onChange={(e) => { const u = [...productLots]; u[index] = { ...u[index], name: e.target.value }; setProductLotsWithSave(u); }} />
                  <TextInput placeholder="Certificate URL" value={lot.certificateUrl}
                    onChange={(e) => { const u = [...productLots]; u[index] = { ...u[index], certificateUrl: e.target.value }; setProductLotsWithSave(u); }} />
                </div>
              ))}
              <Button variant="light" size="xs" leftIcon={<IconPlus size="0.85rem" />}
                onClick={() => setProductLotsWithSave([...productLots, { code: '', name: '', certificateUrl: '' }])}>
                <FormattedMessage id="profile.lots.add" />
              </Button>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="contact">
          <Accordion.Control icon={<IconUsers size="1rem" />}>
            <FormattedMessage id="profile.section.contact" />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack spacing="sm">
              <TextInput label={<FormattedMessage id="profile.contact.name" />} value={contact.name}
                onChange={(e) => setContactWithSave({ ...contact, name: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.contact.phone" />} value={contact.phone}
                onChange={(e) => setContactWithSave({ ...contact, phone: e.target.value })} />
              <TextInput label="Email" value={contact.email}
                onChange={(e) => setContactWithSave({ ...contact, email: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.contact.address" />} value={contact.address}
                onChange={(e) => setContactWithSave({ ...contact, address: e.target.value })} />
              <Divider mt="xs" mb="xs" label={<FormattedMessage id="profile.section.social" />} />
              <TextInput label={<FormattedMessage id="profile.social.instagram" />}
                placeholder="https://instagram.com/yourprofile"
                value={socialLinks.instagram}
                onChange={(e) => setSocialLinksWithSave({ ...socialLinks, instagram: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.social.twitter" />}
                placeholder="https://x.com/yourprofile"
                value={socialLinks.twitter}
                onChange={(e) => setSocialLinksWithSave({ ...socialLinks, twitter: e.target.value })} />
              <TextInput label={<FormattedMessage id="profile.social.website" />}
                placeholder="https://yourwebsite.com"
                value={socialLinks.website}
                onChange={(e) => setSocialLinksWithSave({ ...socialLinks, website: e.target.value })} />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

      </Accordion>

      <div style={{ marginTop: '20px', paddingBottom: '8px' }}>
        <Button fullWidth color={saveStatus === 'saved' ? 'teal' : 'teal'} onClick={saveProfile}
          loading={saveStatus === 'saving'}
          leftIcon={saveStatus === 'saved' ? <IconCheck size="1rem" /> : <IconDownload size="1rem" />}>
          {saveStatus === 'saved' ? '✓ Saved' : <FormattedMessage id="profile.button.save.share" />}
        </Button>
        <Button
          fullWidth
          variant="subtle"
          color="gray"
          mt="xs"
          component="a"
          href={`/profile/${accountId}`}
          target="_blank"
          rel="noopener noreferrer"
          rightIcon={<IconExternalLink size="0.85rem" />}>
          <FormattedMessage id="profile.button.viewprofile" defaultMessage="View My Profile" />
        </Button>
      </div>

      {/* ── PREVIEW ──────────────────────────────────────────── */}
      {hasCustomTemplate && previewMode === 'custom' ? (
        <div style={{ marginTop: '16px', paddingBottom: '16px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontFamily: "'Montserrat', sans-serif" }}>
            <FormattedMessage id="profile.preview.custom.title" defaultMessage="Custom Template Preview" />
          </div>
          <div style={{ width: '100%', height: '400px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
            <iframe
              src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/ProfilePresentation.html`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Custom Profile Presentation Preview"
            />
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '16px', paddingBottom: '16px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', fontFamily: "'Montserrat', sans-serif" }}>
            <FormattedMessage id="profile.preview.title" defaultMessage="Live Preview" />
          </div>
          <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', fontSize: '0.82rem' }}>

            {/* Hero Image */}
            {heroImage && (
              <img src={heroImage} alt="Hero" style={{ width: '100%', height: '120px', objectFit: 'cover', display: 'block' }} />
            )}

            {/* Header gradient */}
            <div style={{
              background: headerColor === 'brown'  ? 'linear-gradient(135deg, #6B4423 0%, #3d2817 100%)' :
                          headerColor === 'green'  ? 'linear-gradient(135deg, #2d5016 0%, #1a3d0f 100%)' :
                          headerColor === 'blue'   ? 'linear-gradient(135deg, #1e3a8a 0%, #0c1d4d 100%)' :
                          headerColor === 'orange' ? 'linear-gradient(135deg, #c2410c 0%, #7c2d12 100%)' :
                          headerColor === 'purple' ? 'linear-gradient(135deg, #6b21a8 0%, #4c1d95 100%)' :
                          headerColor === 'teal'   ? 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)' :
                          headerColor === 'red'    ? 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)' :
                          headerColor === 'slate'  ? 'linear-gradient(135deg, #475569 0%, #1e293b 100%)' :
                                                     'linear-gradient(135deg, #6B4423 0%, #3d2817 100%)',
              color: 'white',
              padding: '1.25rem 1rem',
              textAlign: 'center',
            }}>
              {title    && <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '4px' }}>{title}</div>}
              {subtitle && <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>{subtitle}</div>}
              {!title && !subtitle && (
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Header Preview</div>
              )}
            </div>

            <div style={{ padding: '10px 12px' }}>
              {/* Description */}
              {description && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '4px' }}>
                    <FormattedMessage id="profile.about" defaultMessage="About" />
                  </div>
                  <div style={{ fontSize: '0.78rem', lineHeight: 1.5, color: '#333', whiteSpace: 'pre-wrap' }}>{description}</div>
                </div>
              )}

              {/* Info Grid */}
              {infoGrid.filter(i => i.value).length > 0 && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '6px' }}>
                    <FormattedMessage id="profile.section.infogrid" defaultMessage="Details" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    {infoGrid.filter(i => i.value).map((item, idx) => (
                      <div key={idx}>
                        <div style={{ fontSize: '0.7rem', color: '#888', fontWeight: 500 }}>{item.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#333' }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              {location?.address && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '4px' }}>
                    📍 <FormattedMessage id="profile.location" defaultMessage="Location" />
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#333' }}>{location.address}</div>
                  {location.description && <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '2px' }}>{location.description}</div>}
                </div>
              )}

              {/* Gallery */}
              {gallery.length > 0 && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '6px' }}>
                    🖼️ <FormattedMessage id="profile.status.gallery" defaultMessage="Gallery" /> ({gallery.length})
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {gallery.slice(0, 4).map((img, idx) => (
                      <img key={idx} src={img} alt="" style={{ width: '52px', height: '40px', objectFit: 'cover', borderRadius: '4px', flex: '0 0 auto' }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Product Lots */}
              {productLots.length > 0 && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '4px' }}>
                    📦 <FormattedMessage id="profile.section.lots" defaultMessage="Product Lots" /> ({productLots.length})
                  </div>
                  {productLots.slice(0, 2).map((lot, idx) => (
                    <div key={idx} style={{ fontSize: '0.75rem', color: '#333' }}>• {lot.name || lot.code}</div>
                  ))}
                </div>
              )}

              {/* Contact */}
              {contact?.name && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '4px' }}>
                    📞 <FormattedMessage id="profile.status.contact" defaultMessage="Contact" />
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#333' }}>{contact.name}</div>
                  {contact.phone && <div style={{ fontSize: '0.72rem', color: '#555' }}>{contact.phone}</div>}
                  {contact.email && <div style={{ fontSize: '0.72rem', color: '#555' }}>{contact.email}</div>}
                </div>
              )}

              {/* Social Links */}
              {(socialLinks?.instagram || socialLinks?.twitter || socialLinks?.website) && (
                <div style={{ background: '#f9f9f9', borderRadius: '6px', padding: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.72rem', color: '#555', marginBottom: '4px' }}>
                    🔗 <FormattedMessage id="profile.status.social" defaultMessage="Social & Web" />
                  </div>
                  {socialLinks.instagram && <div style={{ fontSize: '0.75rem', color: '#333' }}>📷 Instagram</div>}
                  {socialLinks.twitter   && <div style={{ fontSize: '0.75rem', color: '#333' }}>🐦 X (Twitter)</div>}
                  {socialLinks.website   && <div style={{ fontSize: '0.75rem', color: '#333' }}>🌐 Website</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderFiles = () => (
    <div style={{ padding: '4px 0' }}>
      <div style={{ marginBottom: '16px' }}>
        <DropzoneButton onUploadSuccess={fetchFiles} />
      </div>
      {loadingFiles ? (
        <div style={{ textAlign: 'center', padding: '30px', color: '#9e9e9e', fontSize: '0.85rem' }}>Loading…</div>
      ) : files.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#bbb' }}>
          <IconFolder size={40} stroke={1} style={{ marginBottom: '8px' }} />
          <div style={{ fontSize: '0.85rem' }}>No documents yet</div>
        </div>
      ) : (
        files.map((file) => (
          <div key={file.name} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 12px', marginBottom: '6px', borderRadius: '8px',
            backgroundColor: '#fafafa', border: '1px solid #f0f0f0',
          }}>
            {fileIcon(file.name)}
            <span style={{ flex: 1, fontSize: '0.78rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Montserrat', sans-serif" }}>
              {file.name}
            </span>
            <button onClick={() => window.open(file.url, '_blank')} style={actionBtnStyle} title="View">
              <IconEye size={16} />
            </button>
            <button onClick={() => handleDocumentDownload(file.url, file.name)} style={actionBtnStyle} title="Download">
              <IconDownload size={16} />
            </button>
            <button onClick={() => handleDeleteFile(file.name)} style={{ ...actionBtnStyle, color: '#e53935' }} title="Delete">
              <IconTrash size={16} />
            </button>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Container size="md" style={{ paddingTop: '12px', paddingBottom: '24px' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', marginBottom: '20px' }}>
        {SUB_TABS.map(({ id, Icon, label }) => {
          const isActive = activeTab === id;
          return (
            <button key={id} onClick={() => setActiveTab(id)} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
              padding: '10px 4px', border: 'none', background: 'none', cursor: 'pointer',
              color: isActive ? '#2E7D32' : '#9e9e9e',
              fontFamily: "'Montserrat', sans-serif", fontSize: '0.68rem', letterSpacing: '0.2px',
              fontWeight: isActive ? 600 : 400,
              borderBottom: isActive ? '2px solid #2E7D32' : '2px solid transparent',
              transition: 'color 0.15s ease, border-color 0.15s ease',
            }}>
              <Icon size={18} stroke={isActive ? 2 : 1.5} />
              {label}
            </button>
          );
        })}
      </div>
      {activeTab === 'userInfo'            && renderUserInfo()}
      {activeTab === 'profilePresentation' && renderProfile()}
      {activeTab === 'files'               && renderFiles()}
    </Container>
  );
}
